import Editor, { type OnMount } from '@monaco-editor/react';
import { cx } from 'class-variance-authority';
import type * as monacoTypes from 'monaco-editor';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Shimmer } from '../..';
import useIsDarkMode from '../../hooks/use-is-dark-mode';
import { useMonaco } from '../monaco/monaco-provider';
import { getVariantSpecificMonacoConfig } from '../monaco/utils';
import type { TemplateInputProps } from '../template-input/template-input';
import { getJsonThemeName } from './utils/monaco-config';
import { moveFocus } from './utils/template-editor-utils';
import { modelAutocomplete } from './utils/template-utils';

export type TemplateEditorProps = {
  id?: string;
  value?: string;
  onChange?: (val: string) => void;
  height?: number | string;
  disabled?: boolean;
  autocompleteItems: TemplateInputProps['autocompleteItems'];
  placeholder?: string;
  size?: 'default' | 'compact';
  variant?: 'textarea' | 'input';
  className?: string;
  hasError?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  bottomPadding?: number;
};

export type TemplateEditorRef = {
  focus: () => void;
};

const TemplateEditor = forwardRef<TemplateEditorRef, TemplateEditorProps>(
  (
    {
      id,
      value = '',
      onChange,
      height = 150,
      disabled = false,
      autocompleteItems,
      placeholder,
      size = 'compact',
      variant = 'textarea',
      className,
      hasError,
      onFocus,
      onBlur,
      autoFocus = false,
      bottomPadding,
    },
    ref,
  ) => {
    const { monaco } = useMonaco();
    const isDarkMode = useIsDarkMode();
    const themeName = getJsonThemeName(isDarkMode);
    const [isFocused, setIsFocused] = useState(false);
    const [isEditorMounted, setIsEditorMounted] = useState(false);
    const mountedModelRef = useRef<monacoTypes.editor.ITextModel | null>(null);
    const editorRef = useRef<monacoTypes.editor.IStandaloneCodeEditor | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Expose focus method to parent via ref (needed for react-hook-form's setFocus)
    useImperativeHandle(ref, () => ({
      focus: () => {
        editorRef.current?.focus();
      },
    }));

    // Calculate height based on variant
    const editorHeight = variant === 'input' ? (size === 'compact' ? 32 : 40) : height;

    const onMount: OnMount = editor => {
      if (!monaco) return;
      editorRef.current = editor;

      if (autoFocus && !disabled) {
        requestAnimationFrame(() => {
          editor.focus();
          const m = editor.getModel();
          if (m) {
            const lineNumber = m.getLineCount();
            const column = m.getLineMaxColumn(lineNumber);
            editor.setPosition({ lineNumber, column });
            editor.revealPositionInCenterIfOutsideViewport({ lineNumber, column });
          }
        });
      }

      // Track the model currently attached to this editor
      const model = editor.getModel();
      if (model) {
        mountedModelRef.current = model;
        modelAutocomplete.set(model, autocompleteItems ?? {});
      }

      // Keep the per-model entry updated if the editor switches models
      editor.onDidChangeModel(e => {
        // remove old one
        if (e.oldModelUrl && mountedModelRef.current) {
          modelAutocomplete.delete(mountedModelRef.current);
        }
        // add new one
        const newModel = editor.getModel();
        mountedModelRef.current = newModel ?? null;
        if (newModel) {
          modelAutocomplete.set(newModel, autocompleteItems ?? {});
        }
      });

      // Create decoration collection (per editor)
      const templateVariableDecorationType = editor.createDecorationsCollection();

      const highlightTemplateVariables = () => {
        const m = editor.getModel();
        if (!m) return;

        const text = m.getValue();
        const decorations: monacoTypes.editor.IModelDeltaDecoration[] = [];

        // Match both complete {{ ... }} and unclosed {{ ...
        const completeRegex = /\{\{\s*[^}]+\s*\}\}/g;
        const unclosedRegex = /\{\{(?![^{]*\}\}).*$/gm;

        // First, find all complete template variables
        let match: RegExpExecArray | null;
        while ((match = completeRegex.exec(text)) !== null) {
          const startPos = m.getPositionAt(match.index);
          const endPos = m.getPositionAt(match.index + match[0].length);

          decorations.push({
            range: new monaco.Range(startPos.lineNumber, startPos.column, endPos.lineNumber, endPos.column),
            options: {
              className: 'template-variable-decoration',
              inlineClassName: 'template-variable-inline',
              stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
            },
          });
        }

        // Then, find unclosed {{ (those not followed by }} on the same line)
        while ((match = unclosedRegex.exec(text)) !== null) {
          const startPos = m.getPositionAt(match.index);
          const endPos = m.getPositionAt(match.index + match[0].length);

          decorations.push({
            range: new monaco.Range(startPos.lineNumber, startPos.column, endPos.lineNumber, endPos.column),
            options: {
              className: 'template-variable-decoration',
              inlineClassName: 'template-variable-inline',
              stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
            },
          });
        }

        templateVariableDecorationType.set(decorations);
      };

      // Continuously trigger suggestions when inside {{
      editor.onDidChangeModelContent(() => {
        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
          const m = editor.getModel();
          if (!m) return;

          const position = editor.getPosition();
          if (!position) return;

          const line = m.getLineContent(position.lineNumber);
          const beforeCursor = line.substring(0, position.column - 1);
          const openIdx = beforeCursor.lastIndexOf('{{');

          // Check if we're inside {{ without closing }}
          if (openIdx !== -1) {
            const between = beforeCursor.substring(openIdx + 2);
            if (!between.includes('}}')) {
              // We're inside braces, check if suggestions are visible
              const isSuggestVisible = !!document.querySelector('.suggest-widget.visible');
              if (!isSuggestVisible) {
                // Trigger suggestions if not already visible
                editor.trigger('template-editor', 'editor.action.triggerSuggest', {});
              }
            }
          }
        });
      });

      // Variant-specific: single-line "input"
      if (variant === 'input') {
        editor.onKeyDown(e => {
          if (e.keyCode === monaco.KeyCode.Enter) {
            const isSuggestVisible = !!document.querySelector('.suggest-widget.visible');
            if (!isSuggestVisible) {
              e.preventDefault();
              e.stopPropagation();
            }
          }
          if (e.keyCode === monaco.KeyCode.Tab) {
            const isSuggestVisible = !!document.querySelector('.suggest-widget.visible');
            if (!isSuggestVisible) {
              e.preventDefault();
              e.stopPropagation();
              moveFocus(!e.shiftKey); // Shift+Tab goes backwards
            }
            // If suggest is visible, let Monaco handle Tab (accept suggestion)
          }
        });

        editor.onDidPaste(() => {
          const m = editor.getModel();
          if (!m) return;

          const text = m.getValue();
          const singleLineText = text.replace(/\r?\n|\r/g, ' ');
          if (text !== singleLineText) {
            m.setValue(singleLineText);
            const lineCount = m.getLineCount();
            const lastLineLength = m.getLineLength(lineCount);
            editor.setPosition({ lineNumber: lineCount, column: lastLineLength + 1 });
          }
        });
      }

      // Focus tracking
      editor.onDidFocusEditorText(() => {
        setIsFocused(true);
        onFocus?.();
      });
      editor.onDidBlurEditorText(() => {
        setIsFocused(false);
        onBlur?.();
      });
      editor.onDidFocusEditorWidget(() => {
        setIsFocused(true);
        onFocus?.();
      });
      editor.onDidBlurEditorWidget(() => {
        setIsFocused(false);
        onBlur?.();
      });

      highlightTemplateVariables();
      editor.onDidChangeModelContent(() => highlightTemplateVariables());

      editor.onDidChangeModelLanguage(() => {
        setTimeout(highlightTemplateVariables, 0);
      });

      // Held until the initial {{ }} decorations are applied, so `aria-busy` doesn't clear while
      // the editor is still visibly settling.
      setIsEditorMounted(true);
    };

    // Keep this editor's model → autocomplete mapping updated when props change
    useEffect(() => {
      const model = mountedModelRef.current;
      if (model) {
        modelAutocomplete.set(model, autocompleteItems ?? {});
      }
    }, [autocompleteItems]);

    if (!monaco) {
      return <Shimmer className="w-full" height={editorHeight} />;
    }

    const handleFocusProxy = () => {
      editorRef.current?.focus();
    };

    return (
      <div ref={containerRef} className="template-editor-container relative" aria-busy={!isEditorMounted}>
        {id && <input id={id} className="sr-only" onFocus={handleFocusProxy} tabIndex={-1} aria-hidden="true" />}
        <Editor
          theme={themeName}
          className={cx(
            `
            relative w-full rounded-md border border-primary
            outline-none
            hover:border-(--border-color-primary-hover)
            px-4
            [&_.monaco-editor]:absolute!
          `,
            {
              // The background comes from CSS, not the Monaco theme, which can't reference a CSS
              // variable and is global to the page anyway. The two keys stay mutually exclusive:
              // both are `!important` at equal specificity, so Tailwind's sort order would decide
              // the winner rather than this list.
              'bg-primary [&_.monaco-editor-background]:bg-primary!': !disabled,
              'bg-secondary! border-tertiary! opacity-60 [&_.monaco-editor-background]:bg-secondary!': disabled,
              'border-secondary! shadow-focus-accent': !hasError && isFocused,
              'border-error!': hasError,
              'shadow-focus-error hover:shadow-none': hasError,
            },
            className,
          )}
          height={editorHeight}
          language="template"
          value={value}
          onChange={v => onChange?.(v ?? '')}
          onMount={onMount}
          loading={<Shimmer className="w-full h-full" />}
          options={(() => {
            const baseConfig = getVariantSpecificMonacoConfig({ disabled, size, placeholder, variant });
            if (bottomPadding !== undefined && variant === 'textarea') {
              return {
                ...baseConfig,
                padding: { ...(baseConfig.padding ?? {}), bottom: bottomPadding },
              };
            }
            return baseConfig;
          })()}
        />
      </div>
    );
  },
);

TemplateEditor.displayName = 'TemplateEditor';

export default TemplateEditor;
